<template>
  <v-container fluid class="pa-8">
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="white" elevation="2">
          <v-card-text class="py-6">
            <div class="d-flex align-center mb-4">
              <v-icon size="40" color="primary" class="mr-3">mdi-chart-timeline-variant</v-icon>
              <div>
                <h1 class="text-h4 font-weight-bold mb-0">Monitor en Tiempo Real</h1>
                <p class="text-body-2 text--secondary mb-0">Última consulta: {{ new Date().toLocaleString('es-MX') }}</p>
              </div>
            </div>
            
            <v-divider class="my-4"></v-divider>
            
            <p class="text-subtitle-1 font-weight-medium mb-3">Tipo de Sensor</p>
            
            <v-row v-if="filtros.tipoSensor">
              <v-col cols="12">
                <v-card 
                  class="sensor-type-card-clean selected-expanded"
                  outlined
                >
                  <v-card-text class="d-flex align-center pa-4">
                    <v-avatar 
                      :color="tiposSensores.find(t => t.id === filtros.tipoSensor).color" 
                      size="60" 
                      class="mr-4"
                    >
                      <v-icon dark size="32">
                        {{ tiposSensores.find(t => t.id === filtros.tipoSensor).icon }}
                      </v-icon>
                    </v-avatar>
                    
                    <div class="flex-grow-1">
                      <h3 class="text-h6 font-weight-bold mb-1">
                        {{ tiposSensores.find(t => t.id === filtros.tipoSensor).nombre }}
                      </h3>
                      <p class="text-body-2 text--secondary mb-0">
                        {{ tiposSensores.find(t => t.id === filtros.tipoSensor).descripcion }}
                      </p>
                    </div>
                    
                    <v-btn
                      icon
                      color="error"
                      @click="deseleccionarSensor"
                      class="ml-3"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col 
                v-for="tipo in tiposSensores" 
                :key="tipo.id"
                cols="12" sm="6" md="3"
              >
                <v-card 
                  class="sensor-type-card-clean"
                  @click="seleccionarTipoSensor(tipo.id)"
                  hover
                  outlined
                  :disabled="tipo.disabled"
                >
                  <v-card-text class="text-center pa-4">
                    <v-avatar :color="tipo.color" size="50" class="mb-3">
                      <v-icon dark size="28">{{ tipo.icon }}</v-icon>
                    </v-avatar>
                    <h4 class="text-subtitle-1 font-weight-bold mb-1">{{ tipo.nombre }}</h4>
                    <p class="text-caption text--secondary mb-0">{{ tipo.descripcion }}</p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col cols="12">
        <v-btn-toggle
          v-model="vistaMonitor"
          mandatory
          dense
          class="monitor-view-toggle"
        >
          <v-btn value="multi" @click="cambiarVistaMonitor('multi')">
            <v-icon left small>mdi-view-dashboard</v-icon>
            Todas las maquinas
          </v-btn>
          <v-btn value="single" @click="cambiarVistaMonitor('single')">
            <v-icon left small>mdi-chart-line</v-icon>
            Grafica individual
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <GraficaTiempoRealMulti
      v-show="vistaMonitor === 'multi'"
      :active="active && vistaMonitor === 'multi'"
    />

    <div v-show="vistaMonitor === 'single'">

    <v-expand-transition>
      <v-row v-if="filtros.tipoSensor">
        <v-col cols="3">
          <v-select
            v-model="filtros.area"
            label="Área"
            :items="areas"
            item-text="nombre"
            item-value="idArea"
            @change="actualizarInstancias"
            :disabled="!filtros.tipoSensor"
          />
        </v-col>
        <v-col cols="3">
          <v-autocomplete
            v-model="filtros.instancia"
            label="Instancia"
            :items="instanciasFiltradas"
            :disabled="!filtros.area"
            :item-text="marcaModelo"
            item-value="idMaquinaIns"
            @change="actualizarSensores"
          >
            <template v-slot:item="data">
              <v-list-item-content>
                <v-list-item-title>
                  {{ data.item.marca.trim() + " - " + data.item.modelo.trim() }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ data.item.nombre.trim() }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="filtros.clave"
            label="Sensor / Token"
            :items="clavesDisponibles"
            :disabled="!filtros.instancia || loadingClaves"
            :loading="loadingClaves"
            @change="iniciarMonitoreo"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="filtros.ventanaTiempo"
            label="Ventana de Tiempo"
            :items="ventanasTiempo"
            item-text="text"
            item-value="value"
            @change="ajustarVentana"
          />
        </v-col>
      </v-row>
    </v-expand-transition>
    <v-expand-transition>
      <v-row v-if="modoComparacion && estadisticasPorMaquina.length > 0" class="mt-4">
        <v-col cols="12">
          <v-card class="white" elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon left color="purple">mdi-table-large</v-icon>
              <span>Comparativa de Máquinas</span>
              <v-spacer></v-spacer>
              <v-chip small color="purple" dark>
                <v-icon small left>mdi-compare</v-icon>
                {{ estadisticasPorMaquina.length }} máquinas
              </v-chip>
            </v-card-title>
            
            <v-card-text class="pa-0">
              <v-data-table
                :headers="headersTablaComparativa"
                :items="estadisticasPorMaquina"
                :items-per-page="10"
                class="elevation-0"
                dense
                :footer-props="{
                  'items-per-page-options': [5, 10, 20],
                  'items-per-page-text': 'Máquinas por página:'
                }"
              >
                <template v-slot:item.maquina="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-avatar :style="`background-color: ${item.colorHex}`" size="24" class="mr-2">
                      <span class="white--text text-caption">{{ item.index + 1 }}</span>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-bold">{{ item.maquina }}</div>
                      <div class="text-caption text--secondary">[{{ item.token }}]</div>
                    </div>
                  </div>
                </template>
  
                <template v-slot:item.muestras="{ item }">
                  <v-chip small color="blue lighten-4" class="blue--text text--darken-2">
                    <v-icon x-small left>mdi-counter</v-icon>
                    {{ item.muestras }}
                  </v-chip>
                </template>
                
                <template v-slot:item.promedio="{ item }">
                  <div class="d-flex align-center">
                    <span class="text-body-2 font-weight-medium mr-2">{{ item.promedio }}s</span>
                    <v-icon 
                      v-if="item.mejorPromedio" 
                      small 
                      color="success"
                    >
                      mdi-crown
                    </v-icon>
                  </div>
                </template>
                
                <template v-slot:item.minimo="{ item }">
                  <v-chip 
                    small 
                    :color="item.mejorMinimo ? 'success' : 'grey lighten-2'"
                    :dark="item.mejorMinimo"
                  >
                    <v-icon v-if="item.mejorMinimo" x-small left>mdi-star</v-icon>
                    {{ item.minimo }}s
                  </v-chip>
                </template>
                
                <template v-slot:item.maximo="{ item }">
                  <v-chip 
                    small 
                    :color="item.mejorMaximo ? 'success' : 'grey lighten-2'"
                    :dark="item.mejorMaximo"
                  >
                    <v-icon v-if="item.mejorMaximo" x-small left>mdi-star</v-icon>
                    {{ item.maximo }}s
                  </v-chip>
                </template>
                
                <template v-slot:item.desviacion="{ item }">
                  <v-chip 
                    small 
                    :color="getColorDesviacion(item.desviacionNum)"
                  >
                    {{ item.desviacion }}s
                  </v-chip>
                </template>
                
                <template v-slot:item.eficiencia="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="item.eficienciaNum"
                      :color="getColorEficiencia(item.eficienciaNum)"
                      height="20"
                      rounded
                      class="mr-2"
                      style="min-width: 80px;"
                    >
                      <span class="white--text text-caption font-weight-bold">
                        {{ item.eficiencia }}%
                      </span>
                    </v-progress-linear>
                    <v-icon 
                      v-if="item.eficienciaNum >= 95" 
                      small 
                      color="success"
                    >
                      mdi-trophy
                    </v-icon>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-expand-transition>
    <v-row v-if="datosValidos">
      <v-col cols="12" class="d-flex flex-wrap align-center">
        <v-btn
          :color="monitoreando ? 'error' : 'success'"
          @click="toggleMonitoreo"
          class="mr-2 mb-2"
        >
          <v-icon left>{{ monitoreando ? 'mdi-pause' : 'mdi-play' }}</v-icon>
          {{ monitoreando ? 'Pausar' : 'Iniciar' }} Monitoreo
        </v-btn>
        <v-tooltip bottom content-class="tooltip-opaco">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                color="purple"
                @click="abrirModoComparacion"
                class="mr-2 mb-2"
                :disabled="monitoreando"
              >
                <v-icon left>mdi-compare</v-icon>
                Comparar Máquinas
              </v-btn>
            </span>
          </template>

          <div class="d-flex align-center">
            <v-icon
              small
              :color="monitoreando ? 'amber lighten-2' : 'light-blue lighten-3'"
              class="mr-2"
            >
              {{ monitoreando ? 'mdi-alert-circle-outline' : 'mdi-information-outline' }}
            </v-icon>
            <span v-if="monitoreando">Debes pausar el monitoreo para comparar máquinas</span>
            <span v-else>Compara varias máquinas a la vez</span>
          </div>
        </v-tooltip>
        <v-btn
          color="warning"
          @click="limpiarDatos"
          :disabled="!chartMontado"
          class="mr-2 mb-2"
        >
          <v-icon left>mdi-delete-sweep</v-icon>
          Limpiar Datos
        </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="deep-purple"
                v-bind="attrs"
                v-on="on"
                class="mr-2 mb-2"
              >
              <v-icon left>mdi-timer-outline</v-icon>
              Intervalo: {{ intervaloConsulta / 1000 }}s
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="opcion in opcionesIntervalo"
              :key="opcion.valor"
              @click="actualizarIntervalo (opcion.valor)"
              :class="{ 'v-list-item--active': intervaloConsulta === opcion.valor }"
            >
              <v-list-item-avatar>
                <v-icon :color="intervaloConsulta === opcion.valor ? 'deep-purple' : 'grey'">
                  mdi-timer-sand
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ opcion.label }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ opcion.descripcion }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="intervaloConsulta === opcion.valor">
                <v-icon color="success">mdi-check-circle</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="info"
              v-bind="attrs"
              v-on="on"
              class="mr-2 mb-2"
            >
              <v-icon left>{{ tiposGrafica.find(t => t.id === tipoGrafica).icon }}</v-icon>
              Tipo de Gráfica
              <v-icon right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="tipo in tiposGrafica"
              :key="tipo.id"
              @click="cambiarTipoGrafica(tipo.id)"
              :class="{ 'v-list-item--active': tipoGrafica === tipo.id }"
            >
              <v-list-item-avatar>
                <v-icon :color="tipo.color">{{ tipo.icon }}</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ tipo.nombre }}</v-list-item-title>
                <v-list-item-subtitle>{{ tipo.descripcion }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="tipoGrafica === tipo.id">
                <v-icon color="success">mdi-check-circle</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <v-spacer></v-spacer>
        <v-chip color="primary" class="mr-2 mb-2">
          <v-icon left small>mdi-clock-outline</v-icon>
          {{ ultimaActualizacion }}
        </v-chip>
        
        <v-chip :color="conectado ? 'success' : 'error'" class="mb-2">
          <v-icon left small>{{ conectado ? 'mdi-connection' : 'mdi-close-network' }}</v-icon>
          {{ conectado ? 'Conectado' : 'Desconectado' }}
        </v-chip>
      </v-col>
    </v-row>
  </v-expand-transition>

  <v-expand-transition>
      <v-row v-if="datosValidos && estadisticas.muestras > 0" class="mt-4">
        <v-col cols="12">
          <v-card class="white" elevation="2">
            <v-card-text class="pa-4">
              <v-row align="center">
                <v-col cols="12" sm="6" md="2">
                  <div class="d-flex align-center">
                    <v-avatar color="blue lighten-4" size="40" class="mr-3">
                      <v-icon color="blue darken-2">mdi-speedometer</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption text--secondary">Valor Actual</div>
                      <div class="text-h5 font-weight-bold">{{ estadisticas.valorActual.toFixed(2) }}s</div>
                    </div>
                  </div>
                </v-col>
                
                <v-col cols="12" sm="6" md="2">
                  <div class="d-flex align-center">
                    <v-avatar color="green lighten-4" size="40" class="mr-3">
                      <v-icon color="green darken-2">mdi-chart-line</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption text--secondary">Promedio</div>
                      <div class="text-h5 font-weight-bold">{{ estadisticas.promedio.toFixed(2) }}s</div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="2">
                  <div class="d-flex align-center">
                    <v-avatar color="teal lighten-4" size="40" class="mr-3">
                      <v-icon color="teal darken-2">mdi-chart-bell-curve-cumulative</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption text--secondary">Mediana</div>
                      <div class="text-h5 font-weight-bold">{{ estadisticas.mediana.toFixed(2) }}s</div>
                    </div>
                  </div>
                </v-col>
                
                <v-col cols="12" sm="6" md="2">
                  <div class="d-flex align-center">
                    <v-avatar color="orange lighten-4" size="40" class="mr-3">
                      <v-icon color="orange darken-2">mdi-arrow-up-bold</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption text--secondary">Máximo</div>
                      <div class="text-h5 font-weight-bold">{{ estadisticas.maximo.toFixed(2) }}s</div>
                    </div>
                  </div>
                </v-col>
                
                <v-col cols="12" sm="6" md="2">
                  <div class="d-flex align-center">
                    <v-avatar color="purple lighten-4" size="40" class="mr-3">
                      <v-icon color="purple darken-2">mdi-arrow-down-bold</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption text--secondary">Mínimo</div>
                      <div class="text-h5 font-weight-bold">{{ estadisticas.minimo.toFixed(2) }}s</div>
                    </div>
                  </div>
                </v-col>
            
                <v-col cols="12" sm="6" md="4" v-if="tendencia">
                  <div class="d-flex align-center">
                    <v-avatar :color="tendencia.color + ' lighten-4'" size="40" class="mr-3">
                      <v-icon :color="tendencia.color + ' darken-2'">{{ tendencia.icono }}</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption text--secondary">Tendencia</div>
                      <div class="text-h6 font-weight-bold text-capitalize">
                        {{ tendencia.tipo }}
                        <v-chip x-small :color="tendencia.color" dark class="ml-2">
                          {{ tendencia.valor }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  </v-expand-transition>

    <v-expand-transition>
    <v-row v-if="datosValidos" class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <div>
              <div class="text-h6">
                <v-icon left :color="tiposGrafica.find(t => t.id === tipoGrafica).color">
                  {{ tiposGrafica.find(t => t.id === tipoGrafica).icon }}
                </v-icon>
                {{ tiposGrafica.find(t => t.id === tipoGrafica).nombre }} - Token: {{ filtros.clave }}
              </div>
              <div class="text-caption text--secondary">
                {{ tiposGrafica.find(t => t.id === tipoGrafica).descripcion }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-chip small outlined>
              <v-icon small left>mdi-chart-box</v-icon>
              {{ estadisticas.muestras }} muestras
            </v-chip>
          </v-card-title>
          
          <v-card-text>
            <div v-if="!chartMontado" class="text-center pa-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-4">Esperando datos...</p>
            </div>
            
            <apexchart
              v-else
              ref="chart"
              height="450"
              :options="chartOptionsDinamicas"
              :series="series"
            />
          <v-row v-if="tipoGrafica === 'multiple' && estadisticas.muestras > 0" dense class="mt-3">
              <v-col cols="12" sm="4">
                <v-card outlined color="success lighten-5">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center">
                      <v-icon color="success" size="32" class="mr-2">mdi-check-circle</v-icon>
                      <div>
                        <div class="text-caption text--secondary">Ciclos Óptimos</div>
                        <div class="text-h5 font-weight-bold success--text">
                          {{ contarCiclosPorRango().optimos }}
                        </div>
                        <div class="text-caption">
                          &lt; {{ (estadisticas.mediana * 0.99).toFixed(2) }}s (Mediana -1%)
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" sm="4">
                <v-card outlined color="warning lighten-5">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center">
                      <v-icon color="warning" size="32" class="mr-2">mdi-alert</v-icon>
                      <div>
                        <div class="text-caption text--secondary">Ciclos Normales</div>
                        <div class="text-h5 font-weight-bold warning--text">
                          {{ contarCiclosPorRango().normales }}
                        </div>
                        <div class="text-caption">
                          {{ (estadisticas.mediana * 0.99).toFixed(2) }}-{{ (estadisticas.mediana * 1.01).toFixed(2) }}s (±1%)
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" sm="4">
                <v-card outlined color="error lighten-5">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center">
                      <v-icon color="error" size="32" class="mr-2">mdi-alert-circle</v-icon>
                      <div>
                        <div class="text-caption text--secondary">Ciclos Críticos</div>
                        <div class="text-h5 font-weight-bold error--text">
                          {{ contarCiclosPorRango().criticos }}
                        </div>
                        <div class="text-caption">
                          &gt; {{ (estadisticas.mediana * 1.01).toFixed(2) }}s (Mediana +1%)
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-expand-transition>

    <v-row v-if="logDatos.length > 0" class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Últimos puntos recibidos
          </v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, i) in logDatos.slice(-10)" :key="i">
                  <td>{{ p.hora }}</td>
                  <td>{{ p.valor }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="historialSesiones.length > 0" class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-history</v-icon>
            Historial de Sesiones de Monitoreo
            <v-spacer></v-spacer>
            <v-btn small outlined color="error" @click="limpiarHistorial">
              <v-icon small left>mdi-delete</v-icon>
              Limpiar Historial
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-timeline dense>
              <v-timeline-item
                v-for="(sesion, index) in historialSesiones"
                :key="index"
                :color="sesion.color"
                small
                fill-dot
              >
                <template v-slot:icon>
                  <v-icon dark small>{{ sesion.icon }}</v-icon>
                </template>

                <template v-if="!sesion.modoComparacion">
                  <v-card class="elevation-2">
                    <v-card-title class="text-h6 pb-2">
                      <v-chip small :color="sesion.color" dark class="mr-2">
                        {{ sesion.token }}
                      </v-chip>
                      <span class="text-subtitle-2">{{ sesion.maquina }}</span>
                      <v-spacer></v-spacer>
                      <v-chip small outlined>
                        <v-icon small left>mdi-clock-outline</v-icon>
                        {{ sesion.duracion }}
                      </v-chip>
                    </v-card-title>

                    <v-card-text class="pb-2">
                      <v-row dense>
                        <v-col cols="12" sm="6" md="3">
                          <div class="d-flex align-center">
                            <v-icon small color="grey" class="mr-2">mdi-calendar</v-icon>
                            <div>
                              <div class="text-caption text--secondary">Fecha</div>
                              <div class="text-body-2 font-weight-medium">{{ sesion.fecha }}</div>
                            </div>
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6" md="3">
                          <div 
                            class="d-flex align-center valores-clickable"
                            @click="toggleValoresSesion(index)"
                            style="cursor: pointer;"
                          >
                            <v-icon small color="blue" class="mr-2">mdi-counter</v-icon>
                            <div>
                              <div class="text-caption text--secondary">
                                Muestras 
                                <v-icon x-small>{{ sesion.mostrarValores ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                              </div>
                              <div class="text-body-2 font-weight-medium primary--text">
                                {{ sesion.muestras }} valores
                              </div>
                            </div>
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6" md="3">
                          <div class="d-flex align-center">
                            <v-icon small color="green" class="mr-2">mdi-chart-line</v-icon>
                            <div>
                              <div class="text-caption text--secondary">Promedio</div>
                              <div class="text-body-2 font-weight-medium">{{ sesion.promedio }}s</div>
                            </div>
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6" md="2">
                          <div class="d-flex align-center">
                            <v-icon small color="teal" class="mr-2">mdi-chart-bell-curve-cumulative</v-icon>
                            <div>
                              <div class="text-caption text--secondary">Mediana</div>
                              <div class="text-body-2 font-weight-medium">{{ sesion.mediana }}s</div>
                            </div>
                          </div>
                        </v-col>

                        <v-col cols="12" sm="6" md="3">
                          <div class="d-flex align-center">
                            <v-icon small color="orange" class="mr-2">mdi-gauge</v-icon>
                            <div>
                              <div class="text-caption text--secondary">Rango</div>
                              <div class="text-body-2 font-weight-medium">{{ sesion.minimo }}s - {{ sesion.maximo }}s</div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      <v-expand-transition>
                        <div v-if="sesion.mostrarValores" class="mt-3">
                          <v-divider class="mb-3"></v-divider>
                          <div class="d-flex align-center mb-2">
                            <v-icon small color="primary" class="mr-2">mdi-table</v-icon>
                            <span class="text-subtitle-2 font-weight-bold">Valores Recibidos</span>
                            <v-spacer></v-spacer>
                            <v-chip x-small color="primary" outlined>
                              Total: {{ sesion.datosGrafica.length }} puntos
                            </v-chip>
                          </div>

                          <v-data-table
                            :headers="[
                              { text: '#', value: 'index', width: '60px' },
                              { text: 'Hora', value: 'hora', width: '150px' },
                              { text: 'Valor (s)', value: 'valor', align: 'end' }
                            ]"
                            :items="formatearValoresSesion(sesion.datosGrafica)"
                            :items-per-page="10"
                            dense
                            class="elevation-1"
                            :footer-props="{
                              'items-per-page-options': [5, 10, 20, 50],
                              'items-per-page-text': 'Filas por página:'
                            }"
                          >
                            <template v-slot:item.index="{ item }">
                              <v-chip x-small color="grey lighten-2">{{ item.index }}</v-chip>
                            </template>

                            <template v-slot:item.valor="{ item }">
                              <v-chip 
                                small 
                                :color="getColorValor(item.valorNum, sesion.promedio)"
                                dark
                              >
                                {{ item.valor }}
                              </v-chip>
                            </template>
                          </v-data-table>
                        </div>
                      </v-expand-transition>
                      <v-expand-transition>
                        <div v-if="sesion.mostrarGrafica" class="mt-3">
                          <v-divider class="mb-3"></v-divider>
                          <apexchart
                            type="line"
                            height="120"
                            :options="getHistorialChartOptions()"
                            :series="[{ name: sesion.token, data: sesion.datosGrafica }]"
                          />
                        </div>
                      </v-expand-transition>
                    </v-card-text>

                    <v-card-actions>
                      <v-btn
                        x-small
                        text
                        :color="sesion.mostrarValores ? 'primary' : 'grey'"
                        @click="toggleValoresSesion(index)"
                      >
                        <v-icon small left>{{ sesion.mostrarValores ? 'mdi-table-eye-off' : 'mdi-table-eye' }}</v-icon>
                        {{ sesion.mostrarValores ? 'Ocultar' : 'Ver' }} Valores
                      </v-btn>

                      <v-btn
                        x-small
                        text
                        :color="sesion.mostrarGrafica ? 'primary' : 'grey'"
                        @click="toggleGraficaSesion(index)"
                      >
                        <v-icon small left>{{ sesion.mostrarGrafica ? 'mdi-chevron-up' : 'mdi-chart-line' }}</v-icon>
                        {{ sesion.mostrarGrafica ? 'Ocultar' : 'Ver' }} Gráfica
                      </v-btn>

                      <v-spacer></v-spacer>

                      <v-btn x-small text color="error" @click="eliminarSesion(index)">
                        <v-icon small left>mdi-delete</v-icon>
                        Eliminar
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </template>
                <template v-else>
                  <v-card class="elevation-2 pa-4">
                    <v-card-title class="text-h6 pb-2">
                      <v-icon color="purple" class="mr-2">mdi-compare</v-icon>
                      Comparación de Máquinas
                      <v-spacer></v-spacer>
                      <v-chip small color="purple" dark>
                        {{ sesion.datosGrafica.length }} máquinas comparadas
                      </v-chip>
                    </v-card-title>

                    <v-card-text>
                      <v-row dense>
                        <v-col
                          v-for="(serie, idx) in sesion.datosGrafica"
                          :key="idx"
                          cols="12"
                          sm="6"
                          md="4"
                        >
                          <v-card outlined>
                            <v-card-title class="d-flex align-center pb-1">
                              <v-avatar size="28" :color="obtenerColorChip(idx)" class="mr-2">
                                <v-icon small dark>mdi-factory</v-icon>
                              </v-avatar>
                              <span class="font-weight-medium text-body-1">
                                {{ serie.nombre || ('Máquina ' + (idx+1)) }}
                              </span>
                            </v-card-title>

                            <v-card-text class="pt-1 pb-2">
                              <div class="d-flex justify-space-between align-center mb-1">
                                <span class="text-caption text--secondary">Promedio</span>
                                <v-chip x-small color="success" dark>
                                  {{ serie.promedio }}s
                                </v-chip>
                              </div>
                              <div class="d-flex justify-space-between align-center mb-1">
                                <span class="text-caption text--secondary">Rango</span>
                                <v-chip x-small color="info" outlined>
                                  {{ serie.minimo }}s - {{ serie.maximo }}s
                                </v-chip>
                              </div>

                              <apexchart
                                type="line"
                                height="100"
                                :options="getHistorialChartOptions()"
                                :series="[{ name: serie.nombre, data: serie.data }]"
                              />
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        x-small
                        text
                        color="error"
                        @click="eliminarSesion(index)"
                      >
                        <v-icon small left>mdi-delete</v-icon>
                        Eliminar Comparación
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </template>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>


      <v-dialog
          v-model="dialogEliminarHistorial"
          max-width="500"
          persistent
        >
      <v-card>
        <v-card-text class="pa-6">
          <h2 class="text-h5 font-weight-medium mb-3">
            ¿Está seguro que desea eliminar todo el historial?
          </h2>
          
          <p class="text-body-1 text--secondary mb-0">
            Esta acción no se puede revertir y será permanente.
          </p>
          
          <div class="mt-2 text-body-2 text--secondary">
            Se eliminarán <strong>{{ historialSesiones.length }} sesiones</strong> de monitoreo.
          </div>
        </v-card-text>
        
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="success"
            @click="dialogEliminarHistorial = false"
            class="text-uppercase"
          >
            Cancelar
          </v-btn>
          <v-btn
            text
            color="error"
            @click="confirmarLimpiarHistorial"
            class="text-uppercase"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  <v-dialog
    v-model="dialogEliminarSesion"
    max-width="500"
    persistent
  >
  <v-card v-if="sesionAEliminar !== null">
    <v-card-text class="pa-6">
      <h2 class="text-h5 font-weight-medium mb-3">
        ¿Está seguro que desea eliminar esta sesión?
      </h2>
      
      <p class="text-body-1 text--secondary mb-4">
        Esta acción no se puede revertir y será permanente.
      </p>
      
      <v-sheet
        color="grey lighten-4"
        rounded
        class="pa-3"
      >
        <div class="d-flex align-center mb-2">
          <v-chip 
            small 
            :color="historialSesiones[sesionAEliminar].color" 
            dark 
            class="mr-2"
          >
            {{ historialSesiones[sesionAEliminar].token }}
          </v-chip>
          <span class="text-body-2">{{ historialSesiones[sesionAEliminar].maquina }}</span>
        </div>
        <div class="text-caption text--secondary">
          {{ historialSesiones[sesionAEliminar].fecha }} • 
          {{ historialSesiones[sesionAEliminar].muestras }} muestras
        </div>
      </v-sheet>
    </v-card-text>
    
    <v-card-actions class="px-6 pb-4">
      <v-spacer></v-spacer>
      <v-btn
        text
        color="success"
        @click="dialogEliminarSesion = false; sesionAEliminar = null"
        class="text-uppercase"
      >
        Cancelar
      </v-btn>
      <v-btn
        text
        color="error"
        @click="confirmarEliminarSesion"
        class="text-uppercase"
      >
        Eliminar
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

  <v-snackbar
    v-model="snackbar.show"
    :color="snackbar.color"
    :timeout="snackbar.timeout"
    top
    right
  >
    {{ snackbar.text }}
    <template v-slot:action="{ attrs }">
      <v-btn
        text
        v-bind="attrs"
        @click="snackbar.show = false"
      >
        Cerrar
      </v-btn>
    </template>
  </v-snackbar>
  <v-dialog
    v-model="dialogComparacion"
    max-width="700"
    persistent
  >
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon left color="purple">mdi-compare</v-icon>
      <span>Comparar Máquinas</span>
    </v-card-title>
    
    <v-card-text class="pa-6">
      <p class="text-body-2 text--secondary mb-4">
        Selecciona otras máquinas para compararlas con <strong>{{ maquinaActualNombre }}</strong>
      </p>
      
      <v-card outlined color="primary lighten-5" class="mb-4">
        <v-card-text class="pa-3">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">mdi-check-circle</v-icon>
            <div>
              <div class="text-caption text--secondary">Máquina actual</div>
              <div class="text-body-1 font-weight-bold">
                {{ maquinaActualNombre }} [{{ filtros.clave }}]
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
      
      <v-select
        v-model="maquinasSeleccionadas"
        :items="maquinasParaComparar"
        item-text="nombre"
        item-value="id"
        label="Máquinas adicionales para comparar"
        multiple
        chips
        deletable-chips
        outlined
        hint="Selecciona al menos una máquina adicional"
        persistent-hint
      >
        <template v-slot:prepend-item>
          <v-list-item @click="seleccionarTodasMaquinas">
            <v-list-item-action>
              <v-icon :color="maquinasSeleccionadas.length > 0 ? 'primary' : ''">
                {{ iconoSeleccionTodasMaquinas }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Seleccionar todas</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-2"></v-divider>
        </template>
        
        <template v-slot:selection="{ item, index }">
          <v-chip
            v-if="index < 3"
            close
            @click:close="removerMaquina(item.id)"
            :color="obtenerColorChip(index + 1)"
            dark
            small
          >
            {{ item.nombre }}
          </v-chip>
          <span
            v-if="index === 3"
            class="grey--text text-caption ml-2"
          >
            (+{{ maquinasSeleccionadas.length - 3 }} más)
          </span>
        </template>
        
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.marcaModelo }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ item.nombreInstancia }} • Token: {{ item.token }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-select>
      
      <v-card v-if="maquinasSeleccionadas.length > 0" outlined class="mt-4">
        <v-card-subtitle class="pb-2">
          <v-icon small left>mdi-chart-multiple</v-icon>
          Se compararán {{ maquinasSeleccionadas.length + 1 }} máquinas
        </v-card-subtitle>
        <v-card-text class="pt-0">
          <v-chip
            :color="obtenerColorChip(0)"
            dark
            small
            class="mr-2 mb-2"
          >
            <v-icon left x-small>mdi-check-circle</v-icon>
            {{ maquinaActualNombre }} [{{ filtros.clave }}]
          </v-chip>
          
          <v-chip
            v-for="(maqId, index) in maquinasSeleccionadas"
            :key="maqId"
            :color="obtenerColorChip(index + 1)"
            dark
            small
            class="mr-2 mb-2"
          >
            {{ obtenerNombreMaquina(maqId) }}
          </v-chip>
        </v-card-text>
      </v-card>
    </v-card-text>
    
    <v-card-actions class="px-6 pb-4">
      <v-spacer></v-spacer>

      <v-btn
        color="grey darken-1"
        text
        @click="cerrarComparacion"
        class="text-uppercase"
      >
        <v-icon left>mdi-close-circle</v-icon>
        Cancelar
      </v-btn>

      <v-btn
        color="purple"
        dark
        :disabled="maquinasSeleccionadas.length === 0"
        @click="iniciarComparacion"
        class="text-uppercase"
      >
        <v-icon left>mdi-chart-line-variant</v-icon>
        Comparar ({{ maquinasSeleccionadas.length + 1 }} máquinas)
      </v-btn>
    </v-card-actions>

  </v-card>
</v-dialog>
    </div>
</v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";
import Vue from "vue";
import GraficaTiempoRealMulti from "./GraficaTiempoRealMulti.vue";

export default {
  components: {
    GraficaTiempoRealMulti,
  },
  props: {
    active: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      vistaMonitor: 'multi',
      dialogComparacion: false, 
      modoComparacion: false,
      maquinasSeleccionadas: [],   
      tokensSeleccionados: [],
      tokensAComparar: [],
      tiposSensores: [
        {
          id: 'produccion',
          nombre: 'Sensores de Tiempo ciclo',
          icon: 'mdi-factory',
          color: 'blue darken-2'
        },
        {
          id: 'temperatura',
          nombre: 'Sensores de Temperatura',
          icon: 'mdi-thermometer',
          color: 'orange darken-2',
          disabled: true 
        },
        {
          id: 'presion',
          nombre: 'Sensores de flujo electricidad',
          icon: 'mdi-sine-wave',
          color: 'green darken-2',
          disabled: true
        },
        {
          id: 'vibracion',
          nombre: 'Sensores de Vibración',
          icon: 'mdi-waveform',
          color: 'purple darken-2',
          disabled: true
        }
      ],
    tipoGrafica: 'tendencia', 
    
    tiposGrafica: [
      {
        id: 'tendencia',
        nombre: 'Tendencia Simple',
        descripcion: 'Línea continua con promedio',
        icon: 'mdi-chart-line',
        color: 'blue'
      },
      {
        id: 'multiple',
        nombre: 'Análisis de Rangos',
        descripcion: 'Zonas de desempeño coloreadas',
        icon: 'mdi-chart-areaspline',
        color: 'purple'
      },
      {
        id: 'area',
        nombre: 'Área con Gradiente',
        descripcion: 'Vista de área sombreada',
        icon: 'mdi-chart-bell-curve',
        color: 'green'
      }
    ],
    intervaloConsulta: 10000,
    opcionesIntervalo: [
      { 
        label: "Muy rápido (1s)", 
        valor: 1000,
        descripcion: "Actualización cada 1 segundos"
      },
      { 
        label: "Rápido (10s)", 
        valor: 10000,
        descripcion: "Actualización cada 10 segundos (recomendado)"
      },
      { 
        label: "Normal (30s)", 
        valor: 30000,
        descripcion: "Actualización cada 30 segundos"
      },
      { 
        label: "Lento (1 min)", 
        valor: 60000,
        descripcion: "Actualización cada 1 minuto"
      }
    ],
      logDatos: [],
      filtros: {
        tipoSensor: null,
        area: null,
        instancia: null,
        clave: null,
        ventanaTiempo: 300,
      },   
      historialSesiones: [],
      sesionActual: null,
      tendencia: null,
      dialogEliminarHistorial: false,
      dialogEliminarSesion: false,
      sesionAEliminar: null,
      areas: [],
      instancias: [],
      clavesDisponibles: [],
      loadingClaves: false,
      ventanasTiempo: [
        { text: "30 segundos", value: 30 },
        { text: "1 minuto", value: 60 },
        { text: "5 minutos", value: 300 },
        { text: "15 minutos", value: 900 },
        { text: "30 minutos", value: 1800 },
        { text: "1 hora", value: 3600 },
        { text: "8 horas", value: 28800 },
        { text: "12 horas", value: 43200 }, 
      ],
      estadisticas: {
        valorActual: 0,
        promedio: 0,
        mediana: 0,  
        maximo: 0,
        minimo: 0,
        muestras: 0,
      },
      series: [{ name: "", data: [] }],
      chartMontado: false,
      monitoreando: false,
      conectado: false,
      ultimaActualizacion: "---",
      interval: null,
      maxPuntos: 200,
      snackbar: {
      show: false,
      text: '',
      color: 'success',
      timeout: 3000
    }
  };
},
computed: {
  datosValidos() {
    return this.filtros.tipoSensor && this.filtros.area && this.filtros.instancia && this.filtros.clave;
  },

  iconoSeleccionTodos() {
    if (this.tokensSeleccionados.length === 0) return 'mdi-checkbox-blank-outline';
    if (this.tokensSeleccionados.length === this.clavesDisponibles.length) return 'mdi-checkbox-marked';
    return 'mdi-minus-box';
  },
  tokensConNombre() {
    if (!this.filtros.instancia || !this.clavesDisponibles.length) {
      return [];
    }
    
    const maquina = this.instancias.find(i => i.idMaquinaIns === this.filtros.instancia);
    const nombreMaquina = maquina 
      ? `${maquina.marca.trim()} - ${maquina.modelo.trim()}` 
      : 'Máquina';
    
    return this.clavesDisponibles.map(token => ({
      token: token,
      label: `${nombreMaquina} [${token}]`,
      value: token
    }));
  },
  
  instanciasFiltradas() {
    if (!this.filtros.area) return [];
    return this.instancias.filter(i => i.idArea === this.filtros.area);
  },
    tokensConNombre() {
      if (!this.filtros.instancia) return [];
      
      const maquina = this.instancias.find(i => i.idMaquinaIns === this.filtros.instancia);
      const nombreMaquina = maquina ? `${maquina.marca.trim()} - ${maquina.modelo.trim()}` : 'Máquina';
      
      return this.clavesDisponibles.map(token => ({
        token: token,
        label: `${nombreMaquina} [${token}]`,
        value: token
      }));
    },
  
  headersTablaComparativa() {
    return [
      { text: 'Máquina', value: 'maquina', sortable: true, width: '250px' },
      { text: 'Muestras', value: 'muestras', align: 'center' },
      { text: 'Promedio', value: 'promedioNum', align: 'end' },
      { text: 'Mínimo', value: 'minimoNum', align: 'end' },
      { text: 'Máximo', value: 'maximoNum', align: 'end' },
      { text: 'Desv. Est.', value: 'desviacionNum', align: 'end' },
      { text: 'Eficiencia', value: 'eficienciaNum', align: 'center', width: '150px' }
    ];
  },
  
  estadisticasPorMaquina() {
    if (!this.modoComparacion || this.series.length === 0) return [];
    
    const stats = this.series.map((serie, index) => {
      const datos = serie.data.map(d => d.y).filter(y => y != null);
      
      if (datos.length === 0) {
        return {
          index,
          maquina: serie.name || `Máquina ${index + 1}`,
          token: this.tokensAComparar[index]?.token || 'N/A',
          color: this.obtenerColorChip(index),
          colorHex: this.obtenerColorChipHex(index), 
          muestras: 0,
          promedio: '0.00',
          promedioNum: 0,
          minimo: '0.00',
          minimoNum: 0,
          maximo: '0.00',
          maximoNum: 0,
          desviacion: '0.00',
          desviacionNum: 0,
          eficiencia: '0',
          eficienciaNum: 0
        };
      }
      
      const promedio = datos.reduce((a, b) => a + b, 0) / datos.length;
      const minimo = Math.min(...datos);
      const maximo = Math.max(...datos);
      
      const varianza = datos.reduce((sum, val) => sum + Math.pow(val - promedio, 2), 0) / datos.length;
      const desviacion = Math.sqrt(varianza);
      
      const mejorTiempo = Math.min(...this.series.flatMap(s => 
        s.data.map(d => d.y).filter(y => y != null)
      ));
      const eficiencia = mejorTiempo > 0 ? ((mejorTiempo / promedio) * 100) : 0;
      
      return {
        index,
        maquina: serie.name || `Máquina ${index + 1}`,
        token: this.tokensAComparar[index]?.token || 'N/A',
        color: this.obtenerColorChip(index),        
        colorHex: this.obtenerColorChipHex(index),  
        muestras: datos.length,
        promedio: promedio.toFixed(2),
        promedioNum: promedio,
        minimo: minimo.toFixed(2),
        minimoNum: minimo,
        maximo: maximo.toFixed(2),
        maximoNum: maximo,
        desviacion: desviacion.toFixed(2),
        desviacionNum: desviacion,
        eficiencia: eficiencia.toFixed(1),
        eficienciaNum: eficiencia
      };
    });
    
    const mejorPromedio = Math.min(...stats.map(s => s.promedioNum).filter(v => v > 0));
    const mejorMinimo = Math.min(...stats.map(s => s.minimoNum).filter(v => v > 0));
    const mejorMaximo = Math.min(...stats.map(s => s.maximoNum).filter(v => v > 0));
    
    return stats.map(s => ({
      ...s,
      mejorPromedio: s.promedioNum === mejorPromedio && s.promedioNum > 0,
      mejorMinimo: s.minimoNum === mejorMinimo && s.minimoNum > 0,
      mejorMaximo: s.maximoNum === mejorMaximo && s.maximoNum > 0
    }));
  },

  chartOptionsDinamicas() {
    const baseOptions = {
      chart: {
        id: "realtime-chart",
        height: 450,
        animations: {
          enabled: true,
          easing: "linear",
          dynamicAnimation: {
            speed: 300,
          },
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          format: "HH:mm:ss",
        },
      },
      yaxis: {
        title: {
          text: 'Tiempo de Ciclo (segundos)'
        },
        labels: {
          formatter: (val) => val?.toFixed(2) + 's' || "0.00s",
        },
      },
      tooltip: {
        x: {
          format: "dd/MM/yyyy HH:mm:ss",
        },
      },
      legend: {
        show: true,
        position: "top",
      },
    };
    if (this.tipoGrafica === 'tendencia') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "line",
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 5,
        colors: [ 
      '#2E93fA', '#66DA26', '#FF9800', '#9C27B0',
      '#F44336', '#00BCD4', '#3F51B5', '#E91E63',
      '#00E396', '#FEB019'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: {
            size: 8,
          }
        },
        colors: [
      '#2E93fA', '#66DA26', '#FF9800', '#9C27B0',
      '#F44336', '#00BCD4', '#3F51B5', '#E91E63',
      '#00E396', '#FEB019'],
        annotations: {
          yaxis: this.estadisticas.promedio > 0 ? [
            {
              y: this.estadisticas.promedio,
              borderColor: '#00E396',
              strokeDashArray: 4,
              label: {
                borderColor: '#00E396',
                style: {
                  color: '#fff',
                  background: '#00E396',
                },
                text: `Promedio: ${this.estadisticas.promedio.toFixed(2)}s`
              }
            }
          ] : []
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (val) => val?.toFixed(2) + 's',
          },
        }
      };
    }
    if (this.tipoGrafica === 'multiple') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "line",
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 6,
          hover: {
            size: 9,
          }
        },
        colors: [
          '#2E93fA', '#66DA26', '#FF9800', '#9C27B0',
          '#F44336', '#00BCD4', '#3F51B5', '#E91E63',
          '#00E396', '#FEB019'
        ],
        annotations: {
          yaxis: this.estadisticas.mediana > 0 ? [
            {
              y: this.estadisticas.mediana,
              borderColor: '#00BCD4',
              strokeDashArray: 4,
              label: {
                borderColor: '#00BCD4',
                style: {
                  color: '#fff',
                  background: '#00BCD4',
                },
                text: `Mediana: ${this.estadisticas.mediana.toFixed(2)}s`
              }
            },
            {
              y: 0,
              y2: this.estadisticas.mediana * 0.99,
              fillColor: '#00E396',
              opacity: 0.1,
            },
            {
              y: this.estadisticas.mediana * 0.99,
              y2: this.estadisticas.mediana * 1.01,
              fillColor: '#FEB019',
              opacity: 0.1,
            },
            {
              y: this.estadisticas.mediana * 1.01,
              y2: this.estadisticas.maximo * 1.1,
              fillColor: '#FF4560',
              opacity: 0.1,
            }
          ] : []
        },
        tooltip: {
          ...baseOptions.tooltip,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const valor = series[seriesIndex][dataPointIndex];
            const mediana = this.estadisticas.mediana;
            const diferencia = valor - mediana;
            const porcentaje = ((diferencia / mediana) * 100).toFixed(1);
            
            // Determinar estado basado en ±1% de la mediana
            let estadoColor = '#00E396';
            let estadoTexto = 'Óptimo';
            
            if (valor > mediana * 1.01) {
              estadoColor = '#FF4560';
              estadoTexto = 'Crítico';
            } else if (valor >= mediana * 0.99 && valor <= mediana * 1.01) {
              estadoColor = '#FEB019';
              estadoTexto = 'Normal';
            }
            
            return `
              <div style="padding: 12px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); min-width: 200px;">
                <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">Tiempo de Ciclo</div>
                <div style="font-size: 24px; font-weight: bold; color: ${estadoColor}; margin-bottom: 8px;">
                  ${valor?.toFixed(2)}s
                </div>
                <div style="background: ${estadoColor}; color: white; padding: 4px 8px; border-radius: 4px; display: inline-block; font-size: 12px; font-weight: bold; margin-bottom: 8px;">
                  ${estadoTexto}
                </div>
                <div style="font-size: 12px; color: #666; margin-top: 8px;">
                  <div>Mediana: ${mediana.toFixed(2)}s</div>
                  <div style="color: ${estadoColor}; font-weight: bold;">
                    ${diferencia >= 0 ? '↑' : '↓'} ${Math.abs(diferencia).toFixed(2)}s (${porcentaje}%)
                  </div>
                  <div style="margin-top: 4px; padding-top: 4px; border-top: 1px solid #eee;">
                    <div style="font-size: 11px;">
                      <span style="color: #00E396;">● Óptimo:</span> &lt; ${(mediana * 0.99).toFixed(2)}s<br>
                      <span style="color: #FEB019;">● Normal:</span> ${(mediana * 0.99).toFixed(2)}s - ${(mediana * 1.01).toFixed(2)}s<br>
                      <span style="color: #FF4560;">● Crítico:</span> &gt; ${(mediana * 1.01).toFixed(2)}s
                    </div>
                  </div>
                </div>
              </div>
            `;
          }
        }
      };
    }

    if (this.tipoGrafica === 'area') {
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "area",
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 90, 100]
          }
        },
        markers: {
          size: 5,
          colors: [
      '#2E93fA', '#66DA26', '#FF9800', '#9C27B0',
      '#F44336', '#00BCD4', '#3F51B5', '#E91E63',
      '#00E396', '#FEB019'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: {
            size: 8,
          }
        },
      colors: [
      '#2E93fA', '#66DA26', '#FF9800', '#9C27B0',
      '#F44336', '#00BCD4', '#3F51B5', '#E91E63',
      '#00E396', '#FEB019'],
        dataLabels: {
          enabled: false
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (val) => val?.toFixed(2) + 's',
          },
        }
      };
    }

    return baseOptions;
  },
    maquinaActualNombre() {
    if (!this.filtros.instancia) return '';
    const maquina = this.instancias.find(i => i.idMaquinaIns === this.filtros.instancia);
    return maquina ? `${maquina.marca.trim()} - ${maquina.modelo.trim()}` : 'Máquina actual';
  },
  
  maquinasParaComparar() {
    if (!this.filtros.area) return [];
    
    return this.instanciasFiltradas
      .filter(m => m.idMaquinaIns !== this.filtros.instancia) 
      .map(m => ({
        id: m.idMaquinaIns,
        nombre: `${m.marca.trim()} - ${m.modelo.trim()}`,
        marcaModelo: `${m.marca.trim()} - ${m.modelo.trim()}`,
        nombreInstancia: m.nombre.trim(),
        token: null
      }));
  },
  
  iconoSeleccionTodasMaquinas() {
    if (this.maquinasSeleccionadas.length === 0) return 'mdi-checkbox-blank-outline';
    if (this.maquinasSeleccionadas.length === this.maquinasParaComparar.length) return 'mdi-checkbox-marked';
    return 'mdi-minus-box';
  },
},
  watch: {
    intervaloConsulta(nuevoValor) {
      if (this.monitoreando) {
        clearInterval(this.interval);
        this.iniciarPolling();
        this.mostrarNotificacion(
          `Intervalo cambiado a ${nuevoValor / 1000} segundo${nuevoValor > 1000 ? 's' : ''}`,
          'info'
        );
      }
    }
  },
  mounted() {
    this.getAreas();
    this.getInstancias();
    this.cargarHistorialLocal();
  },
  beforeDestroy() {
    this.detenerMonitoreo();
  },

 methods: {
    marcaModelo: (item) => item.marca + " - " + item.modelo,
    
    cambiarVistaMonitor(vista) {
      if (this.vistaMonitor === vista) return;
      if (vista === 'multi' && this.monitoreando) {
        this.detenerMonitoreo();
      }
      this.vistaMonitor = vista;
    },
    
    seleccionarTipoSensor(tipo) {
      const tipoObj = this.tiposSensores.find(t => t.id === tipo);
      if (tipoObj && tipoObj.disabled) {
        return;
      }
      
      this.filtros.tipoSensor = tipo;
    },
     actualizarIntervalo(nuevoValor) {
    this.intervaloConsulta = nuevoValor;
     },
    salirModoComparacion() {
      this.detenerMonitoreo();
      this.modoComparacion = false;
      this.maquinasSeleccionadas = [];
      this.tokensAComparar = [];
      this.limpiarDatos();
      this.mostrarNotificacion('Modo comparación desactivado', 'info');
    },

    deseleccionarSensor() {
      this.filtros.tipoSensor = null;
      this.filtros.area = null;
      this.filtros.instancia = null;
      this.filtros.clave = null;
      this.clavesDisponibles = [];
      
      if (this.modoComparacion) {
        this.modoComparacion = false;
        this.maquinasSeleccionadas = [];
        this.tokensAComparar = [];
      }
      
      if (this.monitoreando) {
        this.detenerMonitoreo();
      }
      
      this.limpiarDatos();
      
      this.mostrarNotificacion('Tipo de sensor deseleccionado', 'info');
    },
    
    getAreas() {
      axios.get("/Areas")
        .then(resp => { this.areas = resp.data; })
        .catch(err => console.error("Error al obtener áreas:", err));
    },
    
    getInstancias() {
      axios.get("/MaquinaInstancias")
        .then(resp => { this.instancias = resp.data; })
        .catch(err => console.error("Error al obtener instancias:", err));
    },
    
    async actualizarSensores() {
  if (this.monitoreando && this.sesionActual) this.finalizarSesion();
  
  this.filtros.clave = null;
  this.clavesDisponibles = [];
  
  if (this.modoComparacion) {
    this.modoComparacion = false;
    this.maquinasSeleccionadas = [];
    this.tokensAComparar = [];
    this.mostrarNotificacion('Modo comparación desactivado - instancia cambiada', 'info');
  }
  
  this.detenerMonitoreo();
  this.limpiarDatos();
  
  if (!this.filtros.instancia) return;
  
  this.loadingClaves = true;
  try {
    const resp = await axios.get("/SensorDatos/TokensPorMaquina", {
      params: { idMaquinaIns: this.filtros.instancia }
    });
    this.clavesDisponibles = resp.data;
  } catch (err) {
    console.error("Error al obtener tokens:", err);
  } finally {
    this.loadingClaves = false;
  }
},


    
    async actualizarSensores() {
      this.filtros.clave = null;
      this.clavesDisponibles = [];
      
      if (this.modoComparacion) {
        this.modoComparacion = false;
        this.maquinasSeleccionadas = [];
        this.tokensAComparar = [];
        this.mostrarNotificacion('Modo comparación desactivado - instancia cambiada', 'info');
      }
      
      this.detenerMonitoreo();
      this.limpiarDatos();
      
      if (!this.filtros.instancia) return;
      
      this.loadingClaves = true;
      try {
        const resp = await axios.get("/SensorDatos/TokensPorMaquina", {
          params: {
            idMaquinaIns: this.filtros.instancia
          }
        });
        
        this.clavesDisponibles = resp.data;
      } catch (err) {
        console.error("Error al obtener tokens:", err);
      } finally {
        this.loadingClaves = false;
      }
    },

  toggleValoresSesion(index) {
    this.$set(this.historialSesiones[index], 'mostrarValores', !this.historialSesiones[index].mostrarValores);
  },
  
  formatearValoresSesion(datosGrafica) {
    return datosGrafica.map((punto, idx) => ({
      index: idx + 1,
      hora: moment(punto.x).format('HH:mm:ss'),
      valor: punto.y.toFixed(2) + 's',
      valorNum: punto.y
    }));
  },
    getColorDesviacion(desviacion) {
    if (desviacion < 2) return 'success';
    if (desviacion < 5) return 'warning';
    return 'error';
  },
  
  getColorEficiencia(eficiencia) {
    if (eficiencia >= 95) return 'success';
    if (eficiencia >= 85) return 'info';
    if (eficiencia >= 70) return 'warning';
    return 'error';
  },
  
  getColorValor(valor, promedioStr) {
    const promedio = parseFloat(promedioStr);
    const diff = Math.abs(valor - promedio);
    const porcentaje = (diff / promedio) * 100;
    
    if (porcentaje < 5) return 'success'; //
    if (porcentaje < 15) return 'warning'; 
    return 'error'; 
  },
    
    iniciarMonitoreo() {
      if (!this.datosValidos) return;
      this.detenerMonitoreo();
      this.limpiarDatos();
      this.monitoreando = true;
      this.conectado = true;
      this.iniciarSesion();
      this.obtenerDatosIniciales();
      this.iniciarPolling();
    },
    

    detenerMonitoreo() {
      if (this.monitoreando && this.sesionActual) {
        this.finalizarSesion();
      }
      this.monitoreando = false;
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    
    toggleMonitoreo() {
      if (this.monitoreando) {
        this.detenerMonitoreo();
      } else {
        this.iniciarMonitoreo();
      }
    },
    
    limpiarDatos() {
      if (this.modoComparacion) {
        // En modo comparación, solo limpiar las series
        this.series = [];
      } else {
        // Modo simple
        this.series = [{ name: "", data: [] }];
      }
      
      this.logDatos = [];
      this.estadisticas = {
        valorActual: 0,
        promedio: 0,
        maximo: 0,
        minimo: 0,
        muestras: 0,
      };
      this.chartMontado = false;
      this.ultimaActualizacion = "---";
      this.tendencia = null;
      
    },
    
    ajustarVentana() {
      if (this.monitoreando && this.sesionActual) this.finalizarSesion(); // 👈 NUEVO
      if (this.monitoreando) {
        this.detenerMonitoreo();
        this.limpiarDatos();
        this.iniciarMonitoreo();
      }
    },

    
    iniciarPolling() {
      if (this.interval) {
        clearInterval(this.interval);
      }
      
      this.interval = setInterval(() => {
        if (this.monitoreando) {
          this.obtenerNuevosDatos();
        }
      }, this.intervaloConsulta);  
    },

    async obtenerDatosIniciales() {
      try {
        const fin = moment();
        const inicio = moment().subtract(this.filtros.ventanaTiempo, 'seconds');

        const params = {
          inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
          fin: fin.format("YYYY-MM-DD HH:mm:ss"),
        };

        if (this.modoComparacion && this.tokensAComparar && this.tokensAComparar.length > 0) {
          params.idMaquinas = this.tokensAComparar.map(m => m.idMaquina).join(',');
          params.keys = this.tokensAComparar.map(m => m.token).join(',');  
        } else {
          params.idMaquinaIns = this.filtros.instancia;
          params.key = this.filtros.clave;  
        }

        const resp = await axios.get("/SensorDatos/SeriesRealtime", { params });

        const payload = (resp.data.series || []).map(serie => ({
          ...serie,
          data: serie.data
            .map(p => ({
              x: Number(p.x),
              y: Number(p.y)
            }))
            .filter(p => {
              return !isNaN(p.x) && 
                    !isNaN(p.y) && 
                    isFinite(p.x) && 
                    isFinite(p.y) && 
                    p.x > 0 && 
                    p.y > 0;
            })
        })).filter(serie => serie.data.length > 0); 

        if (payload.length > 0) {
          if (this.modoComparacion) {
            this.series = payload;
            this.calcularEstadisticasComparacion();
          } else {
            Vue.set(this.series, 0, {
              name: payload[0].name,
              data: [...payload[0].data]
            });
            this.calcularEstadisticas();
          }
          
          this.chartMontado = true;
          this.ultimaActualizacion = moment().format("HH:mm:ss");
          this.conectado = true;
          
        } else {
          this.chartMontado = true;
          this.mostrarNotificacion('No se encontraron datos válidos en la ventana de tiempo seleccionada', 'warning');
        }
      } catch (err) {
        console.error("Error al obtener datos iniciales:", err);
        this.conectado = false;
        this.chartMontado = true;
      }
    },

    async obtenerNuevosDatos() {
      try {
        const fin = moment();
        let inicio;

        if (this.modoComparacion && this.series.length > 0) {
          const ultimosTimestamps = this.series
            .filter(s => s.data.length > 0)
            .map(s => s.data[s.data.length - 1].x);
          
          if (ultimosTimestamps.length > 0) {
            const ultimoX = Math.max(...ultimosTimestamps);
            inicio = moment(ultimoX).subtract(1, "seconds");
          } else {
            inicio = moment().subtract(5, "seconds");
          }
        } else if (this.series[0] && this.series[0].data.length > 0) {
          const ultimoX = this.series[0].data[this.series[0].data.length - 1].x;
          inicio = moment(ultimoX).subtract(1, "seconds");
        } else {
          inicio = moment().subtract(5, "seconds");
        }

        const params = {
          inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
          fin: fin.format("YYYY-MM-DD HH:mm:ss"),
        };

        if (this.modoComparacion && this.tokensAComparar.length > 0) {
          params.idMaquinas = this.tokensAComparar.map(m => m.idMaquina).join(',');
          params.keys = this.tokensAComparar.map(m => m.token).join(',');
        } else {
          params.idMaquinaIns = this.filtros.instancia;
          params.key = this.filtros.clave;
        }

        const resp = await axios.get("/SensorDatos/SeriesRealtime", { params });

        const payload = (resp.data.series || []).map(serie => ({
          ...serie,
          data: serie.data
            .map(p => ({
              x: Number(p.x),
              y: Number(p.y)
            }))
            .filter(p => {
              return !isNaN(p.x) && 
                    !isNaN(p.y) && 
                    isFinite(p.x) && 
                    isFinite(p.y) && 
                    p.x > 0 && 
                    p.y > 0;
            })
        })).filter(serie => serie.data.length > 0);

        if (payload.length > 0) {
          this.conectado = true;
          
          if (this.modoComparacion) {
            this.agregarNuevosPuntosMultiples(payload);
            this.calcularEstadisticasComparacion();
          } else {
            this.agregarNuevosPuntos(payload[0]);
            this.calcularEstadisticas();
          }
          
          this.ultimaActualizacion = moment().format("HH:mm:ss");
        }
      } catch (err) {
        console.error("Error al obtener nuevos datos:", err);
        this.conectado = false;
      }
    },

  calcularEstadisticas() {
    if (!this.series[0].data.length) return;
    
    const datos = this.series[0].data.map(d => d.y).filter(y => y !== null && y !== undefined);
    
    if (datos.length === 0) return;
    
    this.estadisticas.valorActual = datos[datos.length - 1];
    this.estadisticas.muestras = datos.length;
    this.estadisticas.promedio = datos.reduce((a, b) => a + b, 0) / datos.length;
    this.estadisticas.mediana = this.calcularMediana(datos);
    this.estadisticas.maximo = Math.max(...datos);
    this.estadisticas.minimo = Math.min(...datos);
    this.tendencia = this.calcularTendencia();
    
    this.$nextTick(() => {
      if (this.$refs.chart && this.chartMontado) {
        this.$refs.chart.updateOptions(this.chartOptionsDinamicas, false, true);
      }
    });
  },

  cambiarTipoGrafica(nuevoTipo) {
      this.tipoGrafica = nuevoTipo;
      
      if (this.$refs.chart && this.chartMontado) {
        this.$nextTick(() => {
          this.$refs.chart.updateOptions(this.chartOptionsDinamicas);
        });
      }
      
      this.mostrarNotificacion(
        `Vista cambiada a: ${this.tiposGrafica.find(t => t.id === nuevoTipo).nombre}`,
        'info'
      );
  },
    
    contarCiclosPorRango() {
      if (!this.series[0] || !this.series[0].data.length) {
        return { optimos: 0, normales: 0, criticos: 0 };
      }
      
      const mediana = this.estadisticas.mediana;
      const margen = mediana * 0.01; // ±1%
      const limiteInferior = mediana - margen;
      const limiteSuperior = mediana + margen;
      
      const datos = this.series[0].data.map(d => d.y);
      
      return {
        optimos: datos.filter(v => v < limiteInferior).length,
        normales: datos.filter(v => v >= limiteInferior && v <= limiteSuperior).length,
        criticos: datos.filter(v => v > limiteSuperior).length,
      };
    },

    obtenerLabelToken(token) {
      const tokenData = this.tokensConNombre.find(t => t.value === token);
      return tokenData ? tokenData.label : token;
    },

  obtenerLabelToken(token) {
    const tokenData = this.tokensConNombre.find(t => t.value === token);
    return tokenData ? tokenData.label : token;
  },
  
  abrirModoComparacion() {
    if (!this.filtros.instancia || !this.filtros.clave) {
      this.mostrarNotificacion('Primero selecciona área, instancia y sensor', 'warning');
      return;
    }
    
    this.maquinasSeleccionadas = [];
    this.dialogComparacion = true;
  },
  agregarNuevosPuntos(nuevaSerie) {
  let puntosAgregados = 0;
  
  nuevaSerie.data.forEach(punto => {
    if (isNaN(punto.x) || isNaN(punto.y) || !isFinite(punto.x) || !isFinite(punto.y) || punto.x <= 0 || punto.y <= 0) {
      return; // Saltar este punto
    }

    const normalizado = {
      x: Number(punto.x),
      y: Number(punto.y)
    };

    const existe = this.series[0].data.some(p => p.x === normalizado.x);
    
    if (!existe) {
      this.series[0].data.push(normalizado);
      puntosAgregados++;

      this.logDatos.push({
        hora: moment(normalizado.x).format("HH:mm:ss"),
        valor: normalizado.y.toFixed(2)
      });

      if (this.logDatos.length > 50) {
        this.logDatos.shift();
      }
    }
  });

  if (puntosAgregados > 0) {
    this.series[0].data.sort((a, b) => a.x - b.x);

    const ultimoTimestamp = this.series[0].data.length > 0 
      ? this.series[0].data[this.series[0].data.length - 1].x 
      : Date.now();
    
    const tiempoLimite = ultimoTimestamp - (this.filtros.ventanaTiempo * 1000);
    const datosFiltrados = this.series[0].data.filter(p => p.x >= tiempoLimite);
    
    const datosFinales = datosFiltrados.length > this.maxPuntos 
      ? datosFiltrados.slice(-this.maxPuntos)
      : datosFiltrados;

    Vue.set(this.series[0], 'data', datosFinales);

    this.calcularEstadisticas();

    this.$nextTick(() => {
      if (this.$refs.chart && this.chartMontado) {
        this.$refs.chart.updateSeries([{
          name: this.series[0].name,
          data: this.series[0].data
        }], false);
      }
    });
  }
},
  
  cerrarComparacion() {
    this.dialogComparacion = false;
    this.maquinasSeleccionadas = [];
  },
  
  seleccionarTodasMaquinas() {
    if (this.maquinasSeleccionadas.length === this.maquinasParaComparar.length) {
      this.maquinasSeleccionadas = [];
    } else {
      this.maquinasSeleccionadas = this.maquinasParaComparar.map(m => m.id);
    }
  },
  
  removerMaquina(idMaquina) {
    const index = this.maquinasSeleccionadas.indexOf(idMaquina);
    if (index >= 0) {
      this.maquinasSeleccionadas.splice(index, 1);
    }
  },
    obtenerNombreMaquina(idMaquina) {
    const maquina = this.instancias.find(i => i.idMaquinaIns === idMaquina);
    if (!maquina) return `Máquina ${idMaquina}`;
    
    const maqData = this.maquinasParaComparar.find(m => m.id === idMaquina);
    return maqData ? `${maqData.nombre} [${maqData.token || '?'}]` : `${maquina.marca} - ${maquina.modelo}`;
  },
  
  obtenerColorChip(index) {
    const colores = [
      'blue', 'green', 'orange', 'purple', 
      'red', 'teal', 'indigo', 'pink',
      'cyan', 'lime'
    ];
    return colores[index % colores.length];
  },
  obtenerColorChipHex(index) {
    const coloresHex = [
      '#2E93fA', '#66DA26', '#FF9800', '#9C27B0',
      '#F44336', '#00BCD4', '#3F51B5', '#E91E63',
      '#00E396', '#FEB019'
    ];
    return coloresHex[index % coloresHex.length];
  },
  async obtenerTokensDeMaquinas() {
    const tokensPromises = this.maquinasSeleccionadas.map(async idMaquina => {
      try {
        
        const resp = await axios.get("/SensorDatos/TokensPorMaquina", {
          params: { idMaquinaIns: idMaquina }
        });
        
        const tokens = resp.data;
        
        const token = tokens.length > 0 ? tokens[0] : null;
        
        const maquina = this.instancias.find(i => i.idMaquinaIns === idMaquina);
        const nombreMaquina = maquina 
          ? `${maquina.marca.trim()} - ${maquina.modelo.trim()}` 
          : `Máquina ${idMaquina}`;
        const resultado = {
          idMaquina,
          token,
          nombre: `${nombreMaquina} [${token}]`
        };      
        return resultado;
      } catch (err) {
        return null;
      }
    });
    
    const resultados = await Promise.all(tokensPromises);
    const resultadosFiltrados = resultados.filter(r => r && r.token); 
    return resultadosFiltrados;
  },
  

  async iniciarComparacion() {
    if (this.maquinasSeleccionadas.length === 0) {
      this.mostrarNotificacion('Selecciona al menos una máquina adicional para comparar', 'warning');
      return;
    }
    
    this.dialogComparacion = false;
    
    this.mostrarNotificacion('Obteniendo tokens de las máquinas...', 'info');
    
    const maquinasConTokens = await this.obtenerTokensDeMaquinas();
    
    
    if (maquinasConTokens.length === 0) {
      this.mostrarNotificacion('No se pudieron obtener tokens de las máquinas', 'error');
      return;
    }
    const maquinaActual = {
      idMaquina: this.filtros.instancia,
      token: this.filtros.clave,
      nombre: `${this.maquinaActualNombre} [${this.filtros.clave}]`
    };
    this.tokensAComparar = [maquinaActual, ...maquinasConTokens];
    if (this.monitoreando) {
      this.detenerMonitoreo();
    }
    this.modoComparacion = true;
    this.limpiarDatos();
    await this.obtenerDatosIniciales();
    
    this.monitoreando = true;
    this.conectado = true;
    this.iniciarPolling();
    
    this.mostrarNotificacion(
      `Monitoreando ${this.tokensAComparar.length} máquinas en tiempo real`, 
      'success'
    );
  },
  
  async obtenerDatosComparacion() {
    try {
      const fin = moment();
      const inicio = moment().subtract(this.filtros.ventanaTiempo, 'seconds');

      const resp = await axios.get("/SensorDatos/SeriesRealtime", {
        params: {
          inicio: inicio.format("YYYY-MM-DD HH:mm:ss"),
          fin: fin.format("YYYY-MM-DD HH:mm:ss"),
          idMaquinaIns: this.filtros.instancia,
          keys: this.tokensSeleccionados.join(',')
        },
      });

      const payload = (resp.data.series || []).map(serie => ({
        ...serie,
        data: serie.data.map(p => ({
          x: Number(p.x),
          y: Number(p.y)
        }))
      }));

      if (payload.length > 0) {
        this.series = payload;
        this.calcularEstadisticasComparacion();
        this.chartMontado = true;
        this.ultimaActualizacion = moment().format("HH:mm:ss");
        this.conectado = true;
        this.mostrarNotificacion(`Comparando ${payload.length} sensores`, 'success');
      } else {
        this.chartMontado = true;
        this.mostrarNotificacion('No se encontraron datos para comparar', 'warning');
      }
    } catch (err) {
      console.error("Error al obtener datos de comparación:", err);
      this.mostrarNotificacion('Error al cargar datos de comparación', 'error');
      this.conectado = false;
      this.chartMontado = true;
    }
  },
  
  calcularEstadisticasComparacion() {
    const todosDatos = this.series
      .flatMap(s => s.data.map(d => d.y))
      .filter(y => y != null);
    
    if (todosDatos.length === 0) {
      this.estadisticas = {
        valorActual: 0,
        promedio: 0,
        maximo: 0,
        minimo: 0,
        muestras: 0,
      };
      return;
    }
    
    this.estadisticas.muestras = todosDatos.length;
    this.estadisticas.promedio = todosDatos.reduce((a, b) => a + b, 0) / todosDatos.length;
    this.estadisticas.maximo = Math.max(...todosDatos);
    this.estadisticas.minimo = Math.min(...todosDatos);
    this.estadisticas.valorActual = todosDatos[todosDatos.length - 1];
    this.tendencia = null; // Deshabilitar tendencia en modo comparación
  },

    agregarNuevosPuntos(nuevaSerie) {
      let puntosAgregados = 0;
      
      nuevaSerie.data.forEach(punto => {
        const normalizado = {
          x: Number(punto.x),
          y: Number(punto.y)
        };

        const existe = this.series[0].data.some(p => p.x === normalizado.x);
        
        if (!existe) {
          this.series[0].data.push(normalizado);
          puntosAgregados++;

          this.logDatos.push({
            hora: moment(normalizado.x).format("HH:mm:ss"),
            valor: normalizado.y.toFixed(2)
          });

          if (this.logDatos.length > 50) {
            this.logDatos.shift();
          }
        }
      });

      if (puntosAgregados > 0) {
        this.series[0].data.sort((a, b) => a.x - b.x);

        const ultimoTimestamp = this.series[0].data.length > 0 
          ? this.series[0].data[this.series[0].data.length - 1].x 
          : Date.now();
        
        const tiempoLimite = ultimoTimestamp - (this.filtros.ventanaTiempo * 1000);
        const datosFiltrados = this.series[0].data.filter(p => p.x >= tiempoLimite);
        
        const datosFinales = datosFiltrados.length > this.maxPuntos 
          ? datosFiltrados.slice(-this.maxPuntos)
          : datosFiltrados;

        Vue.set(this.series[0], 'data', datosFinales);

        this.calcularEstadisticas();

        this.$nextTick(() => {
          if (this.$refs.chart && this.chartMontado) {
            this.$refs.chart.updateSeries([{
              name: this.series[0].name,
              data: this.series[0].data
            }], false);
          }
        });
      }
    },
    
  iniciarSesion() {
    const maquina = this.instancias.find(i => i.idMaquinaIns === this.filtros.instancia);
    this.sesionActual = {
      token: this.filtros.clave,
      maquina: maquina ? `${maquina.marca} - ${maquina.modelo}` : 'Desconocida',
      area: this.areas.find(a => a.idArea === this.filtros.area)?.nombre || 'N/A',
      inicio: moment(),
      datosGrafica: [],
      color: 'primary',
      icon: 'mdi-chart-line',
      mostrarGrafica: false,
      mostrarValores: false 
    };
  },
    
  finalizarSesion() {
    if (!this.sesionActual || this.estadisticas.muestras === 0) return;

    const duracionMs = moment().diff(this.sesionActual.inicio);
    const duracion = moment.duration(duracionMs);

    if (this.modoComparacion && this.series.length > 1) {
      const datosComparativos = this.series.map((serie, index) => {
        if (!serie.data || serie.data.length === 0) {
          return null;
        }

        const datos = serie.data.map(d => d.y).filter(y => y != null);
        const promedio = datos.reduce((a, b) => a + b, 0) / datos.length;
        const mediana = this.calcularMediana(datos);  // NUEVO
        const maximo = Math.max(...datos);
        const minimo = Math.min(...datos);

        return {
          nombre: serie.name || `Máquina ${index + 1}`,
          token: this.tokensAComparar[index]?.token || `Token ${index + 1}`,
          promedio: promedio.toFixed(2),
          mediana: mediana.toFixed(2),  // NUEVO
          maximo: maximo.toFixed(2),
          minimo: minimo.toFixed(2),
          data: serie.data.map(p => ({ x: p.x, y: p.y }))
        };
      }).filter(s => s !== null);

      const sesionComparativa = {
        modoComparacion: true,
        fecha: this.sesionActual.inicio.format('DD/MM/YYYY HH:mm'),
        duracion: `${Math.floor(duracion.asMinutes())}m ${duracion.seconds()}s`,
        inicio: this.sesionActual.inicio,
        fin: moment(),
        datosGrafica: datosComparativos,
        color: 'purple',
        icon: 'mdi-compare',
        mostrarGrafica: false,
        mostrarValores: false
      };

      this.historialSesiones.unshift(sesionComparativa);
      this.guardarHistorialLocal();

      if (this.historialSesiones.length > 20) {
        this.historialSesiones = this.historialSesiones.slice(0, 20);
      }

    } else {
      const sesionCompleta = {
        token: this.sesionActual.token,
        maquina: this.sesionActual.maquina,
        area: this.sesionActual.area,
        fecha: this.sesionActual.inicio.format('DD/MM/YYYY HH:mm'),
        duracion: `${Math.floor(duracion.asMinutes())}m ${duracion.seconds()}s`,
        inicio: this.sesionActual.inicio,
        fin: moment(),
        muestras: this.estadisticas.muestras,
        promedio: this.estadisticas.promedio.toFixed(2),
        mediana: this.estadisticas.mediana.toFixed(2),  // NUEVO
        maximo: this.estadisticas.maximo.toFixed(2),
        minimo: this.estadisticas.minimo.toFixed(2),
        datosGrafica: this.series[0].data.map(p => ({ x: p.x, y: p.y })),
        color: this.obtenerColorPorPromedio(this.estadisticas.promedio),
        icon: 'mdi-chart-line',
        mostrarGrafica: false,
        mostrarValores: false,
        modoComparacion: false
      };

      this.historialSesiones.unshift(sesionCompleta);
      this.guardarHistorialLocal();

      if (this.historialSesiones.length > 20) {
        this.historialSesiones = this.historialSesiones.slice(0, 20);
      }
    }

    this.sesionActual = null;
  },

    
    obtenerColorPorPromedio(promedio) {
      if (promedio < 30) return 'success';
      if (promedio < 60) return 'warning';
      return 'error';
    },
    
    toggleGraficaSesion(index) {
      this.historialSesiones[index].mostrarGrafica = !this.historialSesiones[index].mostrarGrafica;
    },
    
    eliminarSesion(index) {
      this.sesionAEliminar = index;
      this.dialogEliminarSesion = true;
    },
  confirmarEliminarSesion() {
    const sesion = this.historialSesiones[this.sesionAEliminar];
    this.historialSesiones.splice(this.sesionAEliminar, 1);
    this.guardarHistorialLocal();
    this.dialogEliminarSesion = false;
    this.sesionAEliminar = null;
    this.mostrarNotificacion(`Sesión "${sesion.token}" eliminada`, 'success');
  },

  limpiarHistorial() {
    this.dialogEliminarHistorial = true;
  },

  confirmarLimpiarHistorial() {
    this.historialSesiones = [];
    this.guardarHistorialLocal();
    this.dialogEliminarHistorial = false;
    this.mostrarNotificacion('Historial eliminado correctamente', 'success');
  },

    mostrarNotificacion(texto, color = 'success') {
    this.snackbar.text = texto;
    this.snackbar.color = color;
    this.snackbar.show = true;
  },

  guardarHistorialLocal() {
      try {
        localStorage.setItem('historialSensores', JSON.stringify(this.historialSesiones));
      } catch (err) {
        console.error('Error al guardar historial:', err);
      }
    },
    
    cargarHistorialLocal() {
      try {
        const historial = localStorage.getItem('historialSensores');
        if (historial) {
          this.historialSesiones = JSON.parse(historial);
        }
      } catch (err) {
        console.error('Error al cargar historial:', err);
      }
    },
    calcularTendencia() {
    if (this.series[0].data.length < 5) return null;
    
    const ultimos10 = this.series[0].data.slice(-10);
    const sumX = ultimos10.reduce((acc, _, i) => acc + i, 0);
    const sumY = ultimos10.reduce((acc, p) => acc + p.y, 0);
    const sumXY = ultimos10.reduce((acc, p, i) => acc + (i * p.y), 0);
    const sumX2 = ultimos10.reduce((acc, _, i) => acc + (i * i), 0);
    
    const n = ultimos10.length;
    const pendiente = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    
    return {
      tipo: pendiente > 0 ? 'ascendente' : 'descendente',
      valor: Math.abs(pendiente).toFixed(4),
      color: pendiente > 0 ? 'error' : 'success',
      icono: pendiente > 0 ? 'mdi-trending-up' : 'mdi-trending-down'
    };
  },

  calcularMediana(datos) {
    if (datos.length === 0) return 0;
    
    const datosOrdenados = [...datos].sort((a, b) => a - b);
    const mitad = Math.floor(datosOrdenados.length / 2);
    
    if (datosOrdenados.length % 2 === 0) {
      return (datosOrdenados[mitad - 1] + datosOrdenados[mitad]) / 2;
    }
    
    return datosOrdenados[mitad];
  },
    
    getHistorialChartOptions() {
      return {
        chart: {
          type: 'line',
          sparkline: {
            enabled: true
          },
          animations: {
            enabled: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        tooltip: {
          enabled: true,
          x: {
            format: 'HH:mm:ss'
          }
        },
        colors: ['#1976D2']
      };
    },
  }
};
</script>

<style scoped>
.monitor-view-toggle {
  box-shadow: none;
}

.monitor-view-toggle .v-btn {
  text-transform: none;
  font-weight: 600;
}

.sensor-type-card-clean {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 8px !important;
  border: 2px solid #e0e0e0 !important;
}

.sensor-type-card-clean:hover:not([disabled]) {
  border-color: #1976D2 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
}

.sensor-type-card-clean.selected {
  border: 2px solid #4CAF50 !important;
  background-color: #f1f8f4 !important;
}

.sensor-type-card-clean.selected-expanded {
  border: 2px solid #4CAF50 !important;
  background: linear-gradient(135deg, #f1f8f4 0%, #ffffff 100%) !important;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.2) !important;
  animation: expandCard 0.3s ease-out;
}

@keyframes expandCard {
  from {
    opacity: 0;
    transform: scaleX(0.9);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}

.sensor-type-card-clean[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.valores-clickable {
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.valores-clickable:hover {
  background-color: rgba(25, 118, 210, 0.08);
  transform: scale(1.02);
}

.valores-clickable:active {
  transform: scale(0.98);
}
.tooltip-opaco {
  background-color: #6a1b9a !important; 
  color: white !important;
  opacity: 1 !important; 
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 8px 12px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 260px;
}
.v-select.v-input {
  margin-bottom: 12px;
}

.v-select-list .v-list-item {
  min-height: 56px !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.v-select-list .v-list-item__title {
  font-weight: 500;
  font-size: 0.95rem;
}

.v-select-list .v-list-item__subtitle {
  font-size: 0.8rem;
  color: #666;
}

.v-chip {
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: 8px;
}

.v-menu__content {
  max-height: 250px !important;
  overflow-y: auto !important;
  z-index: 2000 !important;
}



@media (max-width: 960px) {
  .text-h5 {
    font-size: 1.25rem !important;
  }
}

@media (max-width: 600px) {
  .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .text-h5 {
    font-size: 1.1rem !important;
  }
}
</style>
